import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CREDIT_STATUS_DEFS, CREDIT_STATUS_MAP, type CreditStatus } from '@goyone/shared';
import { api } from '../../lib/api';
import { useAuth } from '../../auth/AuthProvider';
import { useListState } from '../../lib/hooks';
import { dateTime, money, num } from '../../lib/format';
import {
  Badge,
  EmptyState,
  ErrorState,
  PageHead,
  SearchBox,
  Stat,
  TableSkeleton,
} from '../../components/ui';
import { ExportMenu } from '../../components/ExportMenu';
import {
  describeFilters,
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  matchesSearch,
  useLocalFilterTable,
  type FilterDef,
} from '../../components/ColumnFilter';
import { exportDay, exportNotes, type TableExportDoc } from '../../lib/tableExport';

interface Exposure {
  accountId: string;
  accountName: string;
  status: CreditStatus;
  statusLabel: string;
  effectiveLimit: number | null;
  outstanding: number;
  available: number | null;
  usedPercent: number | null;
}

interface Override {
  id: string;
  accountId: string;
  accountName: string;
  document: 'INVOICE' | 'CONTRACT' | 'QUOTATION';
  amount: number;
  excess: number;
  limitAt: number | null;
  outstandingAt: number;
  reason: string;
  createdAt: string;
}

const DOC_LABELS: Record<Override['document'], string> = {
  INVOICE: 'فاتورة',
  CONTRACT: 'عقد',
  QUOTATION: 'عرض سعر',
};

/** أعمدة فلتر العملاء — محلّية؛ «بلا حد» خانةٌ فارغة في أعمدة السقف والمتاح. */
const EXPOSURE_FILTERS: FilterDef[] = [
  { key: 'accountName', label: 'العميل', kind: 'text', sortable: true },
  {
    key: 'status',
    label: 'الحالة',
    kind: 'enum',
    options: CREDIT_STATUS_DEFS.map((s) => ({ value: s.key, label: s.label })),
  },
  { key: 'outstanding', label: 'المستحق', kind: 'number', sortable: true },
  { key: 'effectiveLimit', label: 'السقف', kind: 'number', sortable: true },
  { key: 'available', label: 'المتاح', kind: 'number', sortable: true },
  { key: 'usedPercent', label: 'الاستهلاك ٪', kind: 'number', sortable: true },
];

/** أعمدة فلتر سجل التخويلات — بمعامل `o` في الرابط فلا تختلط بجدول العملاء. */
const OVERRIDE_FILTERS: FilterDef[] = [
  { key: 'createdAt', label: 'التاريخ', kind: 'date', sortable: true },
  { key: 'accountName', label: 'العميل', kind: 'text', sortable: true },
  {
    key: 'document',
    label: 'المستند',
    kind: 'enum',
    options: (Object.keys(DOC_LABELS) as Override['document'][]).map((k) => ({
      value: k,
      label: DOC_LABELS[k],
    })),
  },
  { key: 'amount', label: 'المبلغ', kind: 'number', sortable: true },
  { key: 'excess', label: 'الزائد عن السقف', kind: 'number', sortable: true },
  { key: 'reason', label: 'السبب', kind: 'text', noList: true },
];

export function CreditExposure() {
  const { atLeast } = useAuth();
  const canManage = atLeast('ADMIN');
  const { state, patch } = useListState();
  const [search, setSearch] = useState(state.q ?? '');
  const [oSearch, setOSearch] = useState(state.oq ?? '');

  const exposures = useQuery({
    queryKey: ['credit', 'exposures'],
    queryFn: () => api<Exposure[]>('/finance/credit/exposures'),
  });

  const overrides = useQuery({
    queryKey: ['credit', 'overrides'],
    queryFn: () => api<Override[]>('/finance/credit/overrides'),
  });

  const all = exposures.data ?? [];
  const over = all.filter((r) => r.status === 'OVER');
  const totalExcess = over.reduce((s, r) => s + Math.abs(r.available ?? 0), 0);

  const table = useLocalFilterTable({
    rows: exposures.data,
    defs: EXPOSURE_FILTERS,
    state,
    patch,
    search: search ? (r: Exposure) => matchesSearch(search, r.accountName) : undefined,
  });
  const rows = table.rows;

  const oTable = useLocalFilterTable({
    rows: overrides.data,
    defs: OVERRIDE_FILTERS,
    state,
    patch,
    param: 'o',
    search: oSearch
      ? (o: Override) => matchesSearch(oSearch, o.accountName, o.reason, DOC_LABELS[o.document])
      : undefined,
  });

  const buildExport = (): TableExportDoc => ({
    title: 'متابعة الائتمان',
    sheets: [
      {
        title: 'العملاء',
        period: { label: `كما في ${exportDay(new Date().toISOString())}` },
        sections: [
          {
            columns: [
              { key: 'name', label: 'العميل' },
              { key: 'status', label: 'الحالة' },
              { key: 'outstanding', label: 'المستحق', kind: 'money', total: true },
              { key: 'limit', label: 'السقف', kind: 'money' },
              { key: 'available', label: 'المتاح', kind: 'money' },
              { key: 'used', label: 'الاستهلاك', kind: 'percent' },
            ],
            rows: rows.map((r) => ({
              name: r.accountName,
              status: r.statusLabel,
              outstanding: r.outstanding,
              limit: r.effectiveLimit,
              available: r.available,
              used: r.usedPercent,
            })),
          },
        ],
        notes: exportNotes({
          q: search,
          conditions: describeFilters(EXPOSURE_FILTERS, table.filters),
          count: rows.length,
          total: rows.length,
          truncated: false,
          unit: 'العملاء',
        }),
      },
    ],
  });

  return (
    <div className="page">
      <PageHead title="متابعة الائتمان" subtitle="العملاء المتجاوزون لسقوفهم والمقاربون لها" />

      {exposures.isError && (
        <ErrorState error={exposures.error} onRetry={() => exposures.refetch()} />
      )}

      <div className="stats">
        <Stat label="متجاوزون" value={over.length} />
        <Stat
          label="إجمالي الزائد عن السقوف"
          value={<span className="num neg">{money(totalExcess)}</span>}
        />
        <Stat label="مقاربون للسقف" value={all.filter((r) => r.status === 'NEAR').length} />
        <Stat label="موقوفون" value={all.filter((r) => r.status === 'HOLD').length} />
      </div>

      {/* قواعدُ الإلزام تُضبط في الإعدادات — هنا رابطٌ إليها لا نموذج. */}
      {canManage && (
        <p className="small mute mt-4">
          درجةُ الإلزام والسقفُ الافتراضيّ في{' '}
          <Link to="/settings/credit-rules" className="link">
            الإعدادات ← قواعد الائتمان
          </Link>
          .
        </p>
      )}

      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث باسم العميل"
        />
        <ExportMenu build={buildExport} disabled={!rows.length} />
        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          <FilterButton {...table.button} />
        </span>
      </div>

      <FilterChips {...table.chips} />

      <div className="card">
        <div className="card-head">
          <h2 className="card-title">العملاء</h2>
        </div>

        <FilterScope {...table.scope}>
        <div className="table-wrap sticky-head table-big">
          <table className="table">
            <thead>
              <tr>
                <th>
                  العميل
                  <HeaderFilter colKey="accountName" />
                </th>
                <th>
                  الحالة
                  <HeaderFilter colKey="status" />
                </th>
                <th>
                  المستحق
                  <HeaderFilter colKey="outstanding" />
                </th>
                <th>
                  السقف
                  <HeaderFilter colKey="effectiveLimit" />
                </th>
                <th>
                  المتاح
                  <HeaderFilter colKey="available" />
                </th>
                <th>
                  الاستهلاك
                  <HeaderFilter colKey="usedPercent" />
                </th>
              </tr>
            </thead>
            {exposures.isLoading && <TableSkeleton rows={4} cols={6} />}

            <tbody>
              {rows.map((r) => (
                <tr key={r.accountId}>
                  <td>
                    <Link to={`/crm/accounts/${r.accountId}`} className="cell-strong link">
                      {r.accountName}
                    </Link>
                  </td>
                  <td>
                    <Badge kind={CREDIT_STATUS_MAP[r.status].kind}>{r.statusLabel}</Badge>
                  </td>
                  <td className="num">{money(r.outstanding)}</td>
                  <td className="num cell-mute">
                    {r.effectiveLimit == null ? 'بلا حد' : money(r.effectiveLimit)}
                  </td>
                  <td className={`num${(r.available ?? 0) < 0 ? ' neg' : ' cell-mute'}`}>
                    {r.available == null ? 'بلا حد' : money(r.available)}
                  </td>
                  <td className="num cell-mute">
                    {r.usedPercent == null ? '—' : `${num(r.usedPercent)}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </FilterScope>

        {!exposures.isLoading && all.length === 0 && (
          <EmptyState
            title="لا عميل يقارب سقفه"
            text="تظهر هنا الحالات التي تستدعي انتباهًا وحدها. دليل العملاء كاملًا في إدارة العملاء."
          />
        )}
        {all.length > 0 && rows.length === 0 && (
          <EmptyState title="لا نتائج" text="لا عميل يطابق هذا البحث أو الفلتر." />
        )}
      </div>

      <div className="card mt-4">
        <div className="card-head">
          <h2 className="card-title">سجل التخويلات</h2>
        </div>

        <div className="toolbar" style={{ paddingInline: 'var(--sp-4)' }}>
          <SearchBox
            value={oSearch}
            onChange={(v) => {
              setOSearch(v);
              patch({ oq: v || undefined });
            }}
            placeholder="بحث بالعميل أو السبب"
          />
          <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
            <FilterButton {...oTable.button} />
          </span>
        </div>
        <FilterChips {...oTable.chips} />

        <FilterScope {...oTable.scope}>
        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                <th>
                  التاريخ
                  <HeaderFilter colKey="createdAt" />
                </th>
                <th>
                  العميل
                  <HeaderFilter colKey="accountName" />
                </th>
                <th>
                  المستند
                  <HeaderFilter colKey="document" />
                </th>
                <th>
                  المبلغ
                  <HeaderFilter colKey="amount" />
                </th>
                <th>
                  الزائد عن السقف
                  <HeaderFilter colKey="excess" />
                </th>
                <th>
                  السبب
                  <HeaderFilter colKey="reason" />
                </th>
              </tr>
            </thead>
            {overrides.isLoading && <TableSkeleton rows={3} cols={6} />}

            <tbody>
              {oTable.rows.map((o) => (
                <tr key={o.id}>
                  <td className="cell-mute">{dateTime(o.createdAt)}</td>
                  <td>
                    <Link to={`/crm/accounts/${o.accountId}`} className="link">
                      {o.accountName}
                    </Link>
                  </td>
                  <td className="cell-mute">{DOC_LABELS[o.document]}</td>
                  <td className="num">{money(o.amount)}</td>
                  <td className="num neg">{money(o.excess)}</td>
                  <td>{o.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </FilterScope>

        {!overrides.isLoading && overrides.data?.length === 0 && (
          <EmptyState
            title="لا تخويلات"
            text="يُسجَّل هنا كل بيع أُذن به فوق سقف عميل، باسم من أذن وسببه."
          />
        )}
        {!!overrides.data?.length && oTable.rows.length === 0 && (
          <EmptyState title="لا نتائج" text="لا تخويل يطابق هذا البحث أو الفلتر." />
        )}
      </div>
    </div>
  );
}
